// Deciding what an offline browser still owes the server.
//
// The collaboration client keeps a local copy of the scene while it has changes
// the server has not acknowledged (see web/src/collab/backup.js). If the tab is
// closed, reloaded or the laptop sleeps before those reach the room, the local
// copy is the only place that work exists. On the next `init` frame we compare
// the two and push back anything the server has not caught up with.
//
// Kept in its own file, free of node imports, because both the browser and the
// test suite load it.

import { incomingWins } from "./reconcile.js";

/**
 * Elements the local copy holds that the server's scene does not have, or has an
 * older version of.
 *
 * Uses the same comparison as live reconciliation, so a backup can never undo a
 * newer edit somebody else made while this tab was away: an element only comes
 * back if it would have won had it arrived over the socket.
 *
 * @param {object[]} local   elements from the local backup
 * @param {object[]} server  elements the server sent in its `init` frame
 * @returns {object[]} the elements worth re-sending, in local order
 */
export const unsyncedElements = (local, server) => {
  const known = new Map((server ?? []).map((el) => [el.id, el]));
  return (local ?? []).filter((el) => {
    if (!el || typeof el.id !== "string") return false;
    return incomingWins(known.get(el.id), el);
  });
};

/**
 * Is a backup worth keeping around at all?
 *
 * A backup whose every element the server already has is noise: it will be
 * dropped rather than restored, and holding it only risks resurrecting stale
 * work later.
 */
export const backupIsStale = (local, server) => unsyncedElements(local, server).length === 0;

/**
 * May the local backup be deleted?
 *
 * This lives here, as a rule rather than a condition buried in a socket
 * handler, because getting it wrong is silent and expensive. It got it wrong
 * once: the server's first save frame on opening a board says "saved, nothing
 * pending" — which is a true statement about the SERVER and says nothing about
 * what this browser is still holding — and acting on it deleted the recovery
 * copy at the exact moment it existed to be used.
 *
 * @param {{saved: boolean, error: unknown, queued: number, recoveryDone: boolean}} state
 */
export const canDropBackup = ({ saved, error, queued, recoveryDone }) =>
  Boolean(saved) && !error && (queued ?? 0) === 0 && Boolean(recoveryDone);
